import { SyntheticEvent } from 'react';

import { AppIcon, AppIconProps } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Chip, SxProps, Tab, Tabs, Theme, useMediaQuery } from '@mui/material';

const getHorizontalMenuTabStyles = (variant: HorizontalMenuTabsProps['variant']) => {
  switch (variant) {
    case 'box':
      return {
        container: boxContainerSx,
        tab: boxTabSx,
        tabs: boxTabsSx,
      };
    case 'minimal':
      return {
        container: minimalContainerSx,
        tab: minimalTabSx,
        tabs: minimalTabsSx,
      };
    case 'filter':
      return {
        container: filterContainerSx,
        tab: filterTabSx,
        tabs: filterTabsSx,
        tabIndicator: filterTabIndicatorSx,
      };
  }
};

export type HorizontalMenuTabsProps = Readonly<{
  setValue: (arg0: number) => void;
  tabs: string[];
  value: number;
  chipConfig?: {
    label: string;
    iconName?: AppIconProps['iconName'];
    iconColor?: AppIconProps['color'];
    onClick?: () => void;
  };
  variant: 'minimal' | 'box' | 'filter';

  disabledTabs?: string[];
}>;

export function HorizontalMenuTabs({
  setValue,
  tabs,
  value,
  chipConfig,
  variant,
  disabledTabs,
}: HorizontalMenuTabsProps) {
  const isMediumScreen = useMediaQuery('(max-width: 1201px)');
  const styles = getHorizontalMenuTabStyles(variant);

  const handleChange = (e: SyntheticEvent, newValue: number) => {
    e.preventDefault();
    setValue(newValue);
  };

  return (
    <Box sx={styles.container}>
      <Tabs
        value={value === -1 ? 0 : value}
        onChange={handleChange}
        sx={styles.tabs}
        TabIndicatorProps={{
          sx: styles.tabIndicator,
        }}
      >
        {tabs.map((t) => (
          <Tab
            key={t}
            label={t}
            sx={styles.tab}
            disableRipple
            disabled={disabledTabs?.includes(t)}
          />
        ))}
      </Tabs>

      {chipConfig && (
        <Chip
          variant="pill"
          size="lg"
          type={isMediumScreen ? 'only' : 'leading'}
          label={chipConfig.label}
          icon={
            chipConfig.iconName && (
              <AppIcon
                iconName={chipConfig.iconName}
                diameter="12px"
                color={chipConfig.iconColor || 'Warning/500'}
              />
            )
          }
          sx={chipSx}
          onClick={chipConfig.onClick}
        />
      )}
    </Box>
  );
}

const minimalTabSx: SxProps<Theme> = ({ typography, backrs }) => ({
  ...typography['text-md-semibold'],
  textTransform: 'none',
  color: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  '&.Mui-selected': {
    color: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  },
  '&:hover': {
    color: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  },
});

const boxTabSx: SxProps<Theme> = ({ backrs, typography }) => ({
  border: `1px solid ${backrs.colors['Gray/300']}`,
  '&:first-of-type': {
    borderRadius: '8px 0px 0px 8px',
  },
  '&:last-of-type': {
    borderRadius: '0px 8px 8px 0px',
  },
  ...typography['text-sm-semibold'],
  color: backrs.colors['Gray/700'],
  transition: 'all 0.3s ease-in-out',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  '&.Mui-selected': {
    backgroundColor: `${backrs.colors['Colors/Background/bg-active']}`,
    color: backrs.colors['Gray/700'],
  },
  '&:hover': {
    backgroundColor: `${backrs.colors['Gray/50']}`,
    color: backrs.colors['Gray/700'],
  },
  textTransform: 'none',
});

const minimalContainerSx: SxProps<Theme> = ({ backrs }) => ({
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  alignItems: 'flex-start',
  flexWrap: 'wrap-reverse',
  pt: {
    sm: backrs.spacing['spacing-2xl'],
  },
  display: {
    xs: 'none',
    md: 'flex',
  },
  justifyContent: 'space-between',
  maxWidth: `calc(936px - ${backrs.spacing['spacing-7xl']}) !important`,
  justifyItems: 'center',
  width: '-webkit-fill-available',
  alignSelf: 'center',
  position: 'sticky',
  top: 0,
  backgroundColor: backrs.colors['Beige/25'],
  zIndex: 10,
  mx: {
    xs: backrs.spacing['spacing-4xl'],
  },
  boxSizing: 'border-box',
});

const boxContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Gray/300']}`,
  boxShadow: backrs.shadows['shadow-xs-skeuomorphic'],
  pt: {
    sm: backrs.spacing['spacing-none'],
  },
  position: 'sticky',
  top: 0,
  width: 'fit-content',
  overflow: 'hidden',
});

const minimalTabsSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-md'],
  textTransform: 'none',
  flex: 1,
  '& .MuiTabs-indicator': {
    backgroundColor: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  },
});

const boxTabsSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    padding: backrs.spacing['spacing-none'],
  },
  '& .MuiButtonBase-root': {
    padding: backrs.spacing['spacing-none'],
    paddingX: {
      xs: backrs.spacing['spacing-sm'],
      lg: backrs.spacing['spacing-xl'],
    },
  },
});

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-primary'],
  borderRadius: backrs.radius['radius-md'],
  '& .MuiChip-label': {
    color: backrs.colors['Colors/Text/text-secondary'],
    display: {
      xs: 'none',
      lg: 'block',
    },
  },
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  alignSelf: 'center',
});

const filterContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  backgroundColor: backrs.colors['Colors/Background/bg-secondary-alt'],
  alignItems: 'flex-start',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Beige/950']}`,
  p: backrs.spacing['spacing-xs'],
  position: 'sticky',
  top: 0,

  transition: 'none',
  zIndex: 1,
});

const filterTabIndicatorSx: SxProps<Theme> = ({ backrs }) => ({
  height: '100%',
  top: 0,
  backgroundColor: 'transparent',
  border: `1px solid ${backrs.colors['Beige/950']}`,
  borderRadius: backrs.radius['radius-sm'],
  transition: 'left 0.3s ease, width 0.3s ease',
  zIndex: 1,
});

const filterTabSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-semibold'],
  color: backrs.colors['Colors/Text/text-quaternary'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  transition: 'color 0.5s ease',
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-primary-alt'],
    color: backrs.colors['Colors/Text/text-secondary'],
  },
  textTransform: 'none',
  py: backrs.spacing['spacing-md'],
  px: backrs.spacing['spacing-lg'],
  minWidth: 'unset',
  minHeight: 'unset',
  '&.Mui-selected': {
    color: backrs.colors['Colors/Text/text-secondary'],
  },
  marginRight: { sm: backrs.spacing['spacing-md'] },
  '&:last-of-type': {
    marginRight: backrs.spacing['spacing-none'],
  },
});

const filterTabsSx: SxProps<Theme> = {
  textTransform: 'none',
  flex: 1,
  '&.MuiTabs-root': {
    minWidth: 'unset',
    minHeight: 'unset',
  },
  transition: 'none',
};
